/**
 * music-sequencer/cell-gesture.js — window.MSCellGesture / module.exports
 *
 * ОДИН владелец у каждого жеста на сетке шагов /music-sequencer: касание,
 * щелчок, протяжка, долгое нажатие, правый щелчок.
 *
 * ── Зачем отдельно ───────────────────────────────────────────────────────
 * До 29.09.2026 клетку барабана вели ДВА независимых обработчика одного жеста:
 * `click` переключал клетку, а «рисование» (pointerdown → pointermove) ставило
 * под указателем то же значение — и ПЕРВОЙ ставило саму начальную клетку.
 * Живой палец всегда смещается на доли пикселя, `pointermove` приходит раньше
 * `click`, и `click` переключал клетку ОБРАТНО: касание пустой клетки не
 * ставило удар, касание удара его не снимало. Свайп страницы, начатый на
 * клетке, успевал её «нарисовать» до `pointercancel`: ставил лишний удар или
 * стирал поставленный. Долгое нажатие вели ещё двое: таймер touchstart
 * (гасился любым touchmove, то есть дрожью пальца) и `contextmenu` (на Android
 * он приходит сам) — сила менялась то на ноль ступеней, то на две.
 * Обращение 27.09.2026 (Galaxy A55, приложение Яндекса, экран 320 px): «не
 * добавляется ударные в бит только можно удалить». Замер стендом на странице
 * (касания через настоящий конвейер ввода): касание с дрожью 0,3 px — удар не
 * ставится; свайп страницы с удара — удар стёрт; мышь с дрожью 1 px в щелчке —
 * удар не ставится; одно касание — два шага «Отменить».
 *
 * ── Правила ──────────────────────────────────────────────────────────────
 *   касание / щелчок   переключает клетку. Вердикт «это касание» выносит сам
 *                      браузер событием `click`: оно приходит, только если
 *                      указатель остался на месте и жест не забрала прокрутка.
 *   протяжка           рисует по ряду барабана — только мышью и пером. Пальцем
 *                      протяжка — это прокрутка страницы или сетки, клеток она
 *                      не касается. Рисование начинается, когда указатель
 *                      ВЫШЕЛ из начальной клетки; дрожь внутри неё — щелчок.
 *   долгое нажатие     (палец, HOLD_MS без сдвига дальше SLOP_PX) на ударе —
 *                      сила на одну ступень, сразу, пока палец лежит; на пустой
 *                      клетке — удар ставится, когда палец отпустили на месте.
 *                      `contextmenu` пальца — тот же жест: кто пришёл первым
 *                      (таймер или меню), тот и делает; второй молчит.
 *   правый щелчок      (мышь, перо, клавиша меню) — сила на одну ступень.
 *   один жест          — не больше одного шага «Отменить».
 *
 * Модуль не знает DOM: клетку ему описывает страница ({inst, step, melody, …}),
 * а действия он отдаёт обратно вызовами обработчиков. Поэтому те же правила
 * проверяются в Node (tests/js/music-sequencer-cell-gesture.test.mjs), а на
 * живой странице — стендом tests/playwright/music-sequencer-tap-adds-a-hit.mjs.
 */
(function (root, factory) {
    if (typeof module === 'object' && module.exports) module.exports = factory();
    else root.MSCellGesture = factory();
})(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    // Долгое нажатие: столько же, сколько у системного long press Android по
    // умолчанию (ViewConfiguration 400 мс) — меню Android и наш таймер
    // приходят почти вместе, отсюда правило «кто первый, тот и делает».
    var HOLD_MS = 400;
    // Палец, ушедший дальше, листает, а не держит. Порядок touch slop
    // Android (8 dp) и Chrome (≈15 px): дрожь живого пальца — доли пикселя.
    var SLOP_PX = 10;
    // `click` принадлежит только что законченному жесту, если пришёл вскоре
    // после отпускания; позже — это чужой щелчок (клавиатура, код страницы).
    var CLICK_AFTER_UP_MS = 1000;

    function create(h) {
        var holdMs = h.holdMs || HOLD_MS;
        var slopPx = h.slopPx || SLOP_PX;
        var setTimer = h.setTimer || function (fn, ms) { return setTimeout(fn, ms); };
        var clearTimer = h.clearTimer || function (id) { clearTimeout(id); };
        var g = null;   // жест, который сейчас идёт (или только что кончился)
        // Полевой замер для телеметрии страницы: сколько касаний пальцем несли сдвиг —
        // ровно те, что код до 29.09 терял, — и сколько свайпов начиналось на клетке.
        var counts = { taps: 0, moved: 0, wob: 0, clicks: 0, clicks_moved: 0, scrolls: 0, holds: 0, paints: 0 };
        var dirty = false;
        function count(k) { counts[k]++; dirty = true; }

        function stopHold() {
            if (g && g.timer) { clearTimer(g.timer); g.timer = 0; }
        }
        function hold() {
            if (!g) return;
            g.timer = 0;
            if (g.ended || g.moved || g.pressed || g.held) return;
            count('holds');
            if (h.isOn(g.cell)) { g.pressed = true; h.velocity(g.cell); }
            else g.held = true;
        }

        return {
            /** pointerdown на сетке; cell — клетка под указателем или null. */
            down: function (e, cell) {
                stopHold();
                g = null;
                if (!cell) return;
                var finger = e.pointerType === 'touch';
                if (!finger && e.button !== 0) return;   // правая/средняя кнопка — это contextmenu
                g = {
                    id: e.pointerId, finger: finger, cell: cell,
                    x: e.clientX, y: e.clientY,
                    value: h.isOn(cell) ? 0 : 1,           // что рисует протяжка: противоположное начальной клетке
                    painted: false, pressed: false, held: false, tapped: false,
                    moved: false, ended: false, upAt: 0, timer: 0, moves: 0, wobble: 0
                };
                if (finger) g.timer = setTimer(hold, holdMs);
            },

            /** pointermove; under() лениво отдаёт клетку под указателем (null — не клетка). */
            move: function (e, under) {
                if (!g || g.ended || e.pointerId !== g.id) return;
                var dist = Math.hypot(e.clientX - g.x, e.clientY - g.y);
                g.moves++;
                if (dist > g.wobble) g.wobble = dist;
                if (!g.moved && dist > slopPx) {
                    g.moved = true;
                    stopHold();
                }
                if (g.finger || g.cell.melody) return;   // палец листает; в мелодии одна нота на шаг — не рисуем
                if (!(e.buttons & 1)) return;
                var c = typeof under === 'function' ? under() : under;
                if (!c || c.melody || c.inst !== g.cell.inst) return;
                if (!g.painted) {
                    if (c.step === g.cell.step) return;  // всё ещё в начальной клетке — это щелчок
                    g.painted = true;
                    count('paints');
                    h.paintBegin(g.cell, g.value);
                }
                h.paint(c, g.value);
            },

            /** pointerup — где угодно на странице. */
            up: function (e) {
                if (!g || g.ended || e.pointerId !== g.id) return;
                stopHold();
                g.ended = true;
                g.upAt = e.timeStamp;
                if (g.painted) h.paintEnd();
                if (g.held && !g.moved) { g.tapped = true; h.toggle(g.cell); }
            },

            /** pointercancel — жест забрал браузер (прокрутка, масштаб): клетки не трогаем. */
            cancel: function (e) {
                if (!g || e.pointerId !== g.id) return;
                stopHold();
                if (g.painted && !g.ended) h.paintEnd();
                if (g.finger && !g.ended) count('scrolls');
                g = null;
            },

            /** click на сетке; true — клетка переключена. */
            click: function (e, cell) {
                var own = g && g.ended && e.timeStamp - g.upAt < CLICK_AFTER_UP_MS ? g : null;
                g = null;
                if (own && (own.painted || own.pressed || own.tapped)) return false;   // жест уже сделал своё
                if (!cell) return false;
                if (own && own.finger) {
                    count('taps');
                    if (own.moves) { counts.moved++; if (own.wobble > counts.wob) counts.wob = Math.round(own.wobble * 10) / 10; }
                } else if (own) {
                    count('clicks');
                    if (own.moves) counts.clicks_moved++;
                }
                h.toggle(cell);
                return true;
            },

            /** contextmenu на сетке; true — меню браузера надо погасить. */
            contextmenu: function (e, cell) {
                if (!cell) return false;
                if (g && g.finger && !g.ended) {
                    // Меню от долгого нажатия пальцем: тот же жест, что и наш таймер.
                    if (!g.pressed && !g.held) { stopHold(); hold(); }
                    return true;
                }
                if (h.isOn(cell)) h.velocity(cell);
                return true;
            },

            /** Для стенда и отладки: что сейчас думает владелец жеста. */
            state: function () {
                return g ? { finger: g.finger, painted: g.painted, pressed: g.pressed, held: g.held, moved: g.moved, ended: g.ended } : null;
            },

            /** Накопленный полевой замер; null — с прошлого вызова ничего не изменилось. */
            takeCounts: function () {
                if (!dirty) return null;
                dirty = false;
                var out = {};
                for (var k in counts) out[k] = counts[k];
                return out;
            }
        };
    }

    return { create: create, HOLD_MS: HOLD_MS, SLOP_PX: SLOP_PX, CLICK_AFTER_UP_MS: CLICK_AFTER_UP_MS };
});
